import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { setPushEnabledPref } from "@/hooks/useNotificationsData";
import { toast } from "sonner";

const DISMISS_KEY = "cg_push_prompt_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 jours

const shouldShow = () => {
  if (typeof window === "undefined") return false;
  if (!("Notification" in window)) return false;
  if (Notification.permission !== "default") return false;
  const dismissed = localStorage.getItem(DISMISS_KEY);
  if (dismissed && Date.now() - Number(dismissed) < DISMISS_TTL_MS) return false;
  return true;
};

const PushPermissionPrompt = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Petit délai pour éviter d'apparaître pendant la mise en page initiale
    const t = setTimeout(() => setVisible(shouldShow()), 1500);
    return () => clearTimeout(t);
  }, [user]);

  if (!visible) return null;

  const handleEnable = async () => {
    try {
      const result = await Notification.requestPermission();
      if (result === "granted") {
        setPushEnabledPref(true);
        toast.success("Notifications activées 🔔", {
          description: "Vous recevrez les annonces importantes de Click & Gard.",
        });
      } else {
        toast("Vous pourrez activer plus tard depuis vos paramètres.");
      }
    } catch {
      // ignore
    }
    setVisible(false);
  };

  const handleLater = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  return (
    <div
      data-testid="push-permission-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] w-[min(420px,calc(100vw-2rem))]"
      role="dialog"
      aria-label="Activer les notifications"
    >
      <div className="rounded-2xl border bg-card shadow-xl p-4 flex gap-3 items-start">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Bell className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-sm font-semibold leading-tight">
            Activez les notifications pour recevoir les annonces importantes de Click & Gard
          </p>
          <p className="text-xs text-muted-foreground">
            Réservations, messages et annonces de l'équipe — directement sur votre appareil.
          </p>
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={handleEnable} className="rounded-full">
              Activer
            </Button>
            <Button size="sm" variant="ghost" onClick={handleLater} className="rounded-full">
              Plus tard
            </Button>
          </div>
        </div>
        <button
          onClick={handleLater}
          aria-label="Fermer"
          className="text-muted-foreground hover:text-foreground -mt-1 -mr-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default PushPermissionPrompt;
